import { createSlice } from '@reduxjs/toolkit';
import { dispatch } from '../../store';
import { deleteMethod, get, post, put } from '../../../inteceptor';
import moment from 'moment';

const initialState = {
  error: null,
  isLoading: false,
  // Departments
  departments: [],
  // Categories
  categories: [],
  // Subcategories
  subcategories: [],
  // Documents
  documents: [],
  // Mutation response (add / update / delete)
  addData: {},
};

const slice = createSlice({
  name: 'sop',
  initialState,
  reducers: {
    hasError(state, action) {
      state.error = { message: action.payload, time: moment.now().toString() };
      state.isLoading = false;
    },

    startRequest(state) {
      state.isLoading = true;
    },

    endRequest(state) {
      state.isLoading = false;
    },

    // ---------- Departments ----------
    getDepartmentsSuccess(state, action) {
      state.departments = action.payload.data;
    },

    // ---------- Categories ----------
    getCategoriesSuccess(state, action) {
      state.categories = action.payload.data;
    },

    // ---------- Subcategories ----------
    getSubcategoriesSuccess(state, action) {
      state.subcategories = action.payload.data;
    },

    // ---------- Documents ----------
    getDocumentsSuccess(state, action) {
      state.documents = action.payload.data;
    },

    // Shared mutation success
    mutationSuccess(state, action) {
      state.addData = {
        data: action.payload.data,
        time: moment.now().toString(),
      };
    },

    clearAll(state) {
      state.addData = {};
      state.error = {};
    },

    clearCategories(state) {
      state.categories = [];
    },

    clearSubcategories(state) {
      state.subcategories = [];
    },

    clearDocuments(state) {
      state.documents = [];
    },

    clearMutationState(state) {
      state.addData = {};
    },
  },
});

// Reducer
export default slice.reducer;

export const { clearCategories, clearSubcategories, clearDocuments, clearMutationState } = slice.actions;

// ─────────────────────────────────────────────────────────────────────────────
// DEPARTMENTS
// ─────────────────────────────────────────────────────────────────────────────

export function getSopDepartments() {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await get('sopDepartment');
      if (response.status) {
        dispatch(slice.actions.getDepartmentsSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function addSopDepartment(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await post('sopDepartment', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function updateSopDepartment(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await put('sopDepartment', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function deleteSopDepartment(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await deleteMethod('sopDepartment', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// CATEGORIES
// ─────────────────────────────────────────────────────────────────────────────

export function getSopCategories(departmentId) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await get(`sopCategory?departmentId=${departmentId}`);
      if (response.status) {
        dispatch(slice.actions.getCategoriesSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function addSopCategory(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await post('sopCategory', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function updateSopCategory(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await put('sopCategory', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function deleteSopCategory(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await deleteMethod('sopCategory', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// SUBCATEGORIES
// ─────────────────────────────────────────────────────────────────────────────

export function getSopSubcategories(categoryId) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await get(`sopSubcategory?categoryId=${categoryId}`);
      if (response.status) {
        dispatch(slice.actions.getSubcategoriesSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function addSopSubcategory(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await post('sopSubcategory', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function updateSopSubcategory(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await put('sopSubcategory', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function deleteSopSubcategory(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await deleteMethod('sopSubcategory', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// DOCUMENTS
// ─────────────────────────────────────────────────────────────────────────────

export function getSopDocuments(subcategoryId) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await get(`sopDocument?subcategoryId=${subcategoryId}`);
      if (response.status) {
        dispatch(slice.actions.getDocumentsSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function addSopDocument(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await post('sopDocument', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function updateSopDocument(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await put('sopDocument', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}

export function deleteSopDocument(body) {
  return async () => {
    try {
      dispatch(slice.actions.startRequest());
      const response = await deleteMethod('sopDocument', body);
      if (response.status) {
        dispatch(slice.actions.mutationSuccess(response));
      } else {
        dispatch(slice.actions.hasError(response.message));
      }
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    } finally {
      dispatch(slice.actions.endRequest());
    }
  };
}
